'use client'

// The live Twetch feed, paged by cursor.
//
// useSWRInfinite keys each page on the PREVIOUS page's nextCursor, which is
// exactly how the API paginates; a null cursor is the head. The hook flattens
// pages for the renderer and merges the per-page hydration maps (users,
// reply targets, quotes, media dims) so a row never has to know which page
// it arrived on.

import React from 'react'
import useSWRInfinite from 'swr/infinite'
import event from '@/utils/event'

/// Emitted by any composer after a successful post; every mounted feed
/// instance refetches on it.
export const FEED_REFRESH_EVENT = 'alkanes-feed-refresh'
import { expectFreshFeed, fetchFeedPage, fetchFollowingPage, type TwetchFeedPage, type TwetchPost, type TwetchUser } from './twetch-api'

export interface TwetchFeedData {
	posts: TwetchPost[]
	users: Record<string, TwetchUser>
	replyPosts: Record<string, TwetchPost>
	quotes: Record<string, number>
	quotedPosts: Record<string, TwetchPost>
	mediaDims: Record<string, [number, number]>
	/// True while the FIRST page is in flight and nothing is renderable.
	loadingInitial: boolean
	/// True while a further page is in flight.
	loadingMore: boolean
	/// The first page failed and nothing is renderable — distinct from
	/// "loaded fine and empty", the only state allowed to claim an empty feed.
	failed: boolean
	hasMore: boolean
	loadMore: () => void
	retry: () => void
}

/// Merge pages into flat post list + lookup maps. Exported for tests.
export function mergeFeedPages(pages: TwetchFeedPage[]): {
	posts: TwetchPost[]
	users: Record<string, TwetchUser>
	replyPosts: Record<string, TwetchPost>
	quotes: Record<string, number>
	quotedPosts: Record<string, TwetchPost>
	mediaDims: Record<string, [number, number]>
} {
	const posts: TwetchPost[] = []
	const seen = new Set<number>()
	const users: Record<string, TwetchUser> = {}
	const replyPosts: Record<string, TwetchPost> = {}
	const quotes: Record<string, number> = {}
	const quotedPosts: Record<string, TwetchPost> = {}
	const mediaDims: Record<string, [number, number]> = {}
	for (const page of pages) {
		for (const post of page.data ?? []) {
			// A post can straddle a page boundary when the head moved between
			// fetches; render it once.
			if (!seen.has(post.id)) {
				seen.add(post.id)
				posts.push(post)
			}
		}
		Object.assign(users, page.users ?? {})
		Object.assign(replyPosts, page.replyPosts ?? {})
		Object.assign(quotes, page.quotes ?? {})
		Object.assign(quotedPosts, page.quotedPosts ?? {})
		Object.assign(mediaDims, page.mediaDims ?? {})
	}
	return { posts, users, replyPosts, quotes, quotedPosts, mediaDims }
}

function pageKey(
	assetId: string | null,
	index: number,
	previous: TwetchFeedPage | null
): [string, string | null, string | null] | null {
	if (previous && !previous.nextCursor) {
		return null // the API said this was the last page
	}
	return ['twetch-feed-alkanes', assetId, index === 0 ? null : previous?.nextCursor ?? null]
}

export default function useTwetchFeed(
	active: boolean,
	assetId: string | null = null,
	/// Set to read the posts of the people this Twetch user follows instead of
	/// the Alkanes tag feed.
	followingOf: number | null = null,
	/// The smart-tag namespace `assetId` lives in.
	namespace = 'alkanes'
): TwetchFeedData {
	const { data, error, size, setSize, isValidating, mutate } = useSWRInfinite<TwetchFeedPage>(
		active
			? (index, previous) =>
					pageKey(
						followingOf !== null
							? `following:${followingOf}`
							: namespace === 'alkanes'
							? assetId
							: `${namespace}|${assetId ?? ''}`,
						index,
						previous
					)
			: () => null,
		([, id, cursor]: [string, string | null, string | null]) =>
			followingOf !== null
				? fetchFollowingPage(followingOf, cursor)
				: fetchFeedPage(cursor, namespace === 'alkanes' ? id : assetId, namespace),
		{
			// The head page is where new posts land — poll it so every open
			// browser sees fresh theses without a reload (the tape's cadence);
			// deeper pages are cursor-keyed and only refetch on explicit
			// mutate. mergeFeedPages dedups the boundary a moved head creates.
			refreshInterval: 30_000,
			revalidateFirstPage: true,
			revalidateAll: false,
			revalidateOnFocus: false
		}
	)

	// Any composer's successful post announces itself on the bus; every
	// mounted feed — the social tab, an asset's thesis tab, the rail —
	// refetches immediately instead of waiting out the poll.
	React.useEffect(() => {
		if (!active) {
			return
		}
		let timer: number | undefined
		const refresh = () => {
			expectFreshFeed()
			void mutate()
			// Once more shortly after, in case the tag index trailed the write.
			window.clearTimeout(timer)
			timer = window.setTimeout(() => void mutate(), 4_000)
		}
		event.listen(FEED_REFRESH_EVENT, refresh)
		return () => {
			window.clearTimeout(timer)
			event.removeListener(FEED_REFRESH_EVENT, refresh)
		}
	}, [active, mutate])

	const pages = React.useMemo(() => data ?? [], [data])
	const merged = React.useMemo(() => mergeFeedPages(pages), [pages])

	const loadingInitial = active && !data && !error
	const loadingMore = loadingInitial || (size > 0 && pages.length < size && isValidating && !error)
	const hasMore = pages.length > 0 ? !!pages[pages.length - 1]?.nextCursor : true

	const loadMore = React.useCallback(() => {
		void setSize((s) => s + 1)
	}, [setSize])
	const retry = React.useCallback(() => {
		void mutate()
	}, [mutate])

	return {
		...merged,
		loadingInitial,
		loadingMore,
		failed: pages.length === 0 && error !== undefined,
		hasMore,
		loadMore,
		retry
	}
}
